import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { round3 } from '../../utils/money';
import { useParams, useNavigate } from 'react-router-dom';
import supplierService from '../../services/supplierService';
import factureService from '../../services/factureService';
import paymentService from '../../services/paymentService';
import authService from '../../services/authService';
import PhoneLink from '../../components/PhoneLink';
import { IcArrowLeft, IcPencil, IcTrash, IcClose, IcSave, IcBuilding, IcContact, IcLandmark, IcReceiptText, IcBanknote, IcMail, IcPhone, IcMapPin, IcSearch, IcChevronRight, IcChevronDown, IcChevronUp, IcReceipt, IcCalendar, IcShoppingBag, IcCamera, IcImage, IcExternalLink, IcTruck, IcCheckCircle, IcClock, IcAlertCircle, IcUndo } from '../../theme/icons';
import { BtnSk, CardSkeleton } from '../../components/Skeleton';
import { matchesSearch } from '../../utils/search';

const formatDT = (value) => `${(Number(value) || 0).toFixed(3)} DT`;

// Rendu de deux façons : page /suppliers/:id (route, mobile, lien direct) ou
// panneau depuis la liste Fournisseurs (`supplierId` + `onClose`, plus de page).
const SupplierDetail = ({ supplierId = null, onClose = null }) => {
  const params = useParams();
  const id = supplierId ?? params.id;
  const embedded = typeof onClose === 'function';
  const navigate = useNavigate();
  // Retour à la liste : ferme le panneau, ou revient à la page /suppliers.
  const backToList = () => { if (embedded) onClose(); else navigate('/suppliers'); };
  // Sellers can view a supplier but not edit it or record acomptes.
  const canManage = authService.isManagerOrAdmin();

  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Toggle for the detail cases (Coordonnées / Légales / Bancaires) under the name.
  const [showDetails, setShowDetails] = useState(false);

  // Invoice history
  const [factures, setFactures] = useState([]);
  const [facturesLoading, setFacturesLoading] = useState(true);
  const [historySearch, setHistorySearch] = useState('');
  const [historyStatus, setHistoryStatus] = useState('all'); // all | paid | partial | unpaid
  const [historySort, setHistorySort] = useState('recent'); // recent | oldest | amount

  // Payment history (dated payments to this supplier — dashboard + acomptes)
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);

  // Edit modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '', email: '', phone: '', address: '',
    registre_commercial: '', matricule_fiscale: '', rib: '', bank_name: '',
  });
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  // Payment photo modal — attach/take a photo of the paper invoice/receipt
  // from the « Historique de paiement » rows
  const [photoPayment, setPhotoPayment] = useState(null); // payment being edited, or null
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const cameraInputRef = useRef(null);   // <input capture> → opens the camera
  const galleryInputRef = useRef(null);  // plain file input → gallery/files

  // « Afficher les détails » toggles — each history section starts collapsed
  // (header + totals visible) and the button reveals its list.
  const [showPayments, setShowPayments] = useState(false);
  const [showFactures, setShowFactures] = useState(false);
  // Panneau (mode fournisseur depuis la liste) : un seul cadre d'historique à
  // deux onglets, comme la fiche client (Historique / Historique soin).
  const [historyTab, setHistoryTab] = useState('paiements'); // paiements | factures
  // Une facture s'ouvre en PAGE (/facturation/detail/:id), pas en panneau
  // (demande Ahmed 2026-09-09).
  // La page facture reçoit d'où l'on vient (`backTo`) : son « Retour » ramène
  // sur la liste avec ce panneau rouvert (ou sur la page fiche en mode page).
  const openFacturePage = (facture) => navigate(`/facturation/detail/${facture.id}`, {
    state: { backTo: embedded ? `/suppliers?fiche=${id}` : `/suppliers/${id}` },
  });

  // Acompte modal states
  const [showAcompteModal, setShowAcompteModal] = useState(false);
  const [acompte, setAcompte] = useState('');
  const [acompteError, setAcompteError] = useState(null);
  const [applyingAcompte, setApplyingAcompte] = useState(false);

  const fetchSupplier = useCallback(async () => {
    const data = await supplierService.getSupplierById(id);
    setSupplier(data);
    return data;
  }, [id]);

  const fetchFactures = useCallback(async () => {
    const data = await factureService.getAllFactures({ supplier_id: id });
    setFactures(Array.isArray(data) ? data : []);
  }, [id]);

  const fetchPayments = useCallback(async () => {
    const data = await paymentService.getAll({ payment_type: 'supplier', supplier: id });
    setPayments(Array.isArray(data) ? data : []);
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await fetchSupplier();
        setError(null);
      } catch (err) {
        console.error('Error fetching supplier:', err);
        setError('Impossible de charger ce fournisseur.');
      } finally {
        setLoading(false);
      }
    })();

    (async () => {
      try {
        setFacturesLoading(true);
        await fetchFactures();
      } catch (err) {
        console.error('Error fetching supplier factures:', err);
        setFactures([]);
      } finally {
        setFacturesLoading(false);
      }
    })();

    (async () => {
      try {
        setPaymentsLoading(true);
        await fetchPayments();
      } catch (err) {
        console.error('Error fetching supplier payments:', err);
        setPayments([]);
      } finally {
        setPaymentsLoading(false);
      }
    })();
  }, [fetchSupplier, fetchFactures, fetchPayments]);

  // ---- Payment photo handlers ----
  const openPhotoModal = (payment) => {
    setPhotoError(null);
    setPhotoPayment(payment);
  };

  const onPhotoFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-picking the same file later
    if (!file || !photoPayment) return;
    try {
      setPhotoError(null);
      setUploadingPhoto(true);
      const updated = await paymentService.uploadImage(photoPayment.id, file);
      setPayments((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
      setPhotoPayment((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
    } catch (err) {
      const data = err?.response?.data;
      setPhotoError(data?.error || err?.message || "Impossible d'enregistrer la photo.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!photoPayment || !window.confirm('Supprimer le reçu de ce paiement ?')) return;
    try {
      setPhotoError(null);
      setUploadingPhoto(true);
      const updated = await paymentService.deleteImage(photoPayment.id);
      setPayments((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
      setPhotoPayment((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
    } catch (err) {
      const data = err?.response?.data;
      setPhotoError(data?.error || err?.message || 'Impossible de supprimer la photo.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeleteSupplier = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce fournisseur ?')) return;
    try {
      setDeleting(true);
      await supplierService.deleteSupplier(id);
      backToList();
    } catch (err) {
      console.error('Error deleting supplier:', err);
      alert('Échec de la suppression du fournisseur. Veuillez réessayer.');
      setDeleting(false);
    }
  };

  const handleOpenEditModal = () => {
    setEditFormData({
      name: supplier.name || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      registre_commercial: supplier.registre_commercial || '',
      matricule_fiscale: supplier.matricule_fiscale || '',
      rib: supplier.rib || '',
      bank_name: supplier.bank_name || '',
    });
    setUpdateError(null);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setUpdateError(null);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateSupplier = async (e) => {
    e.preventDefault();
    if (!editFormData.name.trim()) {
      setUpdateError('Le nom du fournisseur est requis.');
      return;
    }
    try {
      setUpdating(true);
      setUpdateError(null);
      const updatedSupplier = await supplierService.updateSupplier(id, editFormData);
      setSupplier(updatedSupplier);
      handleCloseEditModal();
    } catch (err) {
      console.error('Error updating supplier:', err);
      setUpdateError('Une erreur est survenue lors de la mise à jour du fournisseur.');
    } finally {
      setUpdating(false);
    }
  };

  // Status meta (label + colors) — mirrors ListFacturation
  const getStatusMeta = (facture) => {
    const amountDue = Number(facture.amount_due) || 0;
    const total = Number(facture.total) || 0;
    // Etiquettes-2 §3 : symbole + libellé ; barre #FECACA (red-200) pour le
    // partiel, verte à 100 %. `Icon` = vignette d'état de la ligne (Lucide,
    // teintée par la classe du badge) — son absence faisait planter la liste.
    if (facture.is_avoir) {
      return { key: 'avoir', label: 'Avoir', pill: 'bdg-warn', bar: 'bg-gray-200', Icon: IcUndo };
    }
    if (amountDue <= 0) {
      return { key: 'paid', label: 'Payé', pill: 'bdg-pos', bar: 'bg-green-700', Icon: IcCheckCircle };
    } else if (amountDue < total) {
      return { key: 'partial', label: 'Partiel', pill: 'bdg-alert', bar: 'bg-red-200', Icon: IcClock };
    }
    return { key: 'unpaid', label: 'Non payé', pill: 'bdg-critical', bar: 'bg-red-200', Icon: IcAlertCircle };
  };

  // Derived totals from the supplier's factures
  const facturesStats = useMemo(() => {
    return factures.reduce((acc, f) => {
      const total = Number(f.total) || 0;
      const due = Number(f.amount_due) || 0;
      acc.total += total;
      acc.due += due;
      // Avoir (retour fournisseur) : total négatif déduit, jamais « payé ».
      if (f.is_avoir) { acc.avoirCount += 1; return acc; }
      acc.paid += total - due;
      if (due <= 0) acc.paidCount += 1;
      else if (due < total) acc.partialCount += 1;
      else acc.unpaidCount += 1;
      return acc;
    }, { total: 0, paid: 0, due: 0, paidCount: 0, partialCount: 0, unpaidCount: 0, avoirCount: 0 });
  }, [factures]);

  // Total of recorded payments to this supplier
  const paymentsTotal = useMemo(
    () => payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
    [payments],
  );

  // Filtered + sorted invoice history
  const displayedFactures = useMemo(() => {
    let list = factures.filter((f) => {
      const meta = getStatusMeta(f);
      if (historyStatus !== 'all' && meta.key !== historyStatus) return false;
      // Règle Ki Para (utils/search.js) : n° / code-barres « contient »,
      // mode de paiement par début de mot.
      return matchesSearch(historySearch, f.payment_method, [f.number, f.barrecode]);
    });
    list = [...list].sort((a, b) => {
      if (historySort === 'amount') return (Number(b.total) || 0) - (Number(a.total) || 0);
      const da = new Date(a.date_added || 0).getTime();
      const db = new Date(b.date_added || 0).getTime();
      return historySort === 'oldest' ? da - db : db - da;
    });
    return list;
  }, [factures, historySearch, historyStatus, historySort]);

  // ----- Acompte (distributed across unpaid factures, oldest first) -----
  const handleOpenAcompteModal = () => {
    setAcompte('');
    setAcompteError(null);
    setShowAcompteModal(true);
  };

  const handleApplyAcompte = async (e) => {
    e.preventDefault();
    const amount = round3(parseFloat(acompte));
    if (!amount || isNaN(amount) || amount <= 0) {
      setAcompteError('Veuillez entrer un montant valide.');
      return;
    }
    if (amount > facturesStats.due + 1e-6) {
      setAcompteError('Le montant dépasse le reste à payer du fournisseur.');
      return;
    }
    const unpaid = factures
      .filter((f) => (Number(f.amount_due) || 0) > 0)
      .sort((a, b) => new Date(a.date_added || 0) - new Date(b.date_added || 0));

    setApplyingAcompte(true);
    setAcompteError(null);
    const EPS = 1e-6;
    // If the user is paying (essentially) the whole balance, clear it exactly so no
    // sub-millime float dust is left behind on the supplier.
    const payAll = amount >= facturesStats.due - 5e-4;
    let remaining = payAll ? facturesStats.due : amount;
    let totalPaid = 0;
    try {
      for (const f of unpaid) {
        if (remaining <= EPS) break;
        const due = Number(f.amount_due) || 0;
        if (due <= 0) continue;
        // Pay the facture in full when the remaining covers it — send its EXACT
        // amount_due so we never round above it (the backend rejects amount > due,
        // and stored dues can carry >3 decimals from float arithmetic).
        const pay = remaining >= due - EPS ? due : remaining;
        if (pay <= 0) continue;
        await factureService.updateFacturePayment(f.id, { amount: pay, skip_payment_log: true });
        remaining -= pay;
        totalPaid += pay;
      }
      // Record the acompte as a dated payment so it appears in the payment history.
      if (totalPaid > EPS) {
        try {
          await paymentService.create({
            payment_type: 'supplier',
            supplier: id,
            source: 'acompte',
            amount: round3(totalPaid),
            note: 'Acompte sur factures',
          });
        } catch (payErr) {
          // Don't fail the acompte if only the history record couldn't be written.
          console.error('Acompte recorded on factures but payment log failed:', payErr);
        }
      }
      await Promise.all([fetchSupplier(), fetchFactures(), fetchPayments()]);
      setShowAcompteModal(false);
      setAcompte('');
    } catch (err) {
      console.error('Acompte error:', err);
      setAcompteError("Erreur lors de l'enregistrement de l'acompte.");
    } finally {
      setApplyingAcompte(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <CardSkeleton />
      </div>
    );
  }

  if (error || !supplier) {
    return (
      <div className={embedded ? 'w-full' : 'w-full px-4 pt-2 pb-4'}>
        {!embedded && (
          <button
            onClick={backToList}
            className="flex items-center px-5 py-2.5 text-sm mb-4 btn-s"
          >
            <IcArrowLeft className="mr-2" /> Retour
          </button>
        )}
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl" role="alert">
          {error || 'Fournisseur introuvable.'}
        </div>
        {embedded && (
          <div className="mt-4 text-right">
            <button type="button" onClick={onClose} className="btn-g px-4 py-2">Fermer</button>
          </div>
        )}
      </div>
    );
  }

  const amountDue = facturesStats.due;
  const factureCount = supplier.facture_count || factures.length || 0;

  const STATUS_TABS = [
    { key: 'all', label: 'Toutes', count: factures.length },
    { key: 'paid', label: 'Payées', count: facturesStats.paidCount },
    { key: 'partial', label: 'Partielles', count: facturesStats.partialCount },
    { key: 'unpaid', label: 'Non payées', count: facturesStats.unpaidCount },
  ];

  const InfoRow = ({ Icon, label, value, mono }) => (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-white flex items-center justify-center text-gray-400">
        <Icon className="text-sm" />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
        <p className={`text-sm text-gray-800 mt-0.5 break-words ${mono ? 'font-mono' : ''}`}>{value || 'Non renseigné'}</p>
      </div>
    </div>
  );


  // ---------- Modales partagées (page ET panneau) ----------
  const modals = (
    <>
      {/* Hidden pickers for the payment's invoice photo: camera capture + gallery */}
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhotoFile} />
      <input ref={galleryInputRef} type="file" accept="image/*" className="hidden" onChange={onPhotoFile} />

      {/* Payment photo modal */}
      {photoPayment && (
        <div className="modal-backdrop flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="modal-head items-start px-5 py-4">
              <div>
                <h3 className="modal-title">Reçu de paiement</h3>
                <p className="text-xs text-gray-300 mt-0.5">
                  Paiement de {formatDT(photoPayment.amount)}
                  {photoPayment.created_at ? ` · ${new Date(photoPayment.created_at).toLocaleDateString('fr-FR')}` : ''}
                  {photoPayment.note ? ` · ${photoPayment.note}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => !uploadingPhoto && setPhotoPayment(null)}
                aria-label="Fermer"
                className="h-9 w-9 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10"
              >
                <IcClose />
              </button>
            </div>

            <div className="p-5">
              {/* Preview / empty state */}
              {photoPayment.image_url ? (
                <a
                  href={photoPayment.image_url}
                  target="_blank"
                  rel="noreferrer"
                  title="Ouvrir le reçu en grand"
                  className="relative block rounded-xl overflow-hidden border border-gray-200 group/photo"
                >
                  <img
                    src={photoPayment.image_url}
                    alt="Reçu de paiement"
                    className="w-full max-h-72 object-contain bg-gray-50"
                  />
                  <span className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-black/60 text-white text-xs font-medium">
                    <IcExternalLink className="text-[10px]" /> Ouvrir
                  </span>
                </a>
              ) : (
                <div className="rounded-xl border-2 border-dashed border-gray-200 py-10 text-center">
                  <IcImage className="mx-auto text-3xl text-gray-200 mb-2" />
                  <p className="text-sm text-gray-500">Aucun reçu attaché à ce paiement</p>
                </div>
              )}

              {photoError && (
                <p role="alert" className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {photoError}
                </p>
              )}

              {/* Actions */}
              <div className="grid grid-cols-2 gap-3 mt-4">
                <button
                  type="button"
                  disabled={uploadingPhoto}
                  onClick={() => cameraInputRef.current && cameraInputRef.current.click()}
                  className="btn-s px-4 py-3 rounded-xl text-sm"
                >
                  <IcCamera /> Prendre une photo
                </button>
                <button
                  type="button"
                  disabled={uploadingPhoto}
                  onClick={() => galleryInputRef.current && galleryInputRef.current.click()}
                  className="btn-s px-4 py-3 rounded-xl text-sm"
                >
                  <IcImage /> Choisir une image
                </button>
              </div>

              {uploadingPhoto && (
                <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-500">
                  <BtnSk />
                  Enregistrement de la photo…
                </div>
              )}

              {photoPayment.image_url && !uploadingPhoto && (
                <button
                  type="button"
                  onClick={handleDeletePhoto}
                  className="w-full mt-3 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm btn-db"
                >
                  <IcTrash /> Supprimer le reçu
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Acompte Modal */}
      {showAcompteModal && (
        <div className="modal-backdrop flex items-center justify-center z-50 p-4" onClick={() => setShowAcompteModal(false)}>
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head -mx-6 -mt-6 mb-4 rounded-t-2xl px-6">
              <h2 className="modal-title text-lg">Enregistrer un acompte</h2>
              <button onClick={() => setShowAcompteModal(false)} className="text-gray-400 hover:text-white" aria-label="Fermer"><IcClose /></button>
            </div>
            <p className="text-sm text-gray-500 mb-5">{supplier.name}</p>

            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Reste à payer (total)</span>
                <span className="font-semibold text-red-600">{formatDT(facturesStats.due)}</span>
              </div>
              <p className="text-xs text-gray-400 pt-1">
                L'acompte est appliqué aux factures impayées, des plus anciennes aux plus récentes.
              </p>
            </div>

            <form onSubmit={handleApplyAcompte}>
              {acompteError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mt-4">{acompteError}</div>
              )}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-900 mb-1.5">Montant de l'acompte</label>
                <div className="relative">
                  <input
                    type="number"
                    value={acompte}
                    onChange={(e) => setAcompte(e.target.value)}
                    min="0"
                    max={facturesStats.due}
                    step="0.001"
                    className="inp w-full px-4 py-3 rounded-xl text-base text-gray-900"
                    placeholder="0.000"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">DT</span>
                </div>
              </div>

              {/* Composants-2 §10 : pied à droite — Annuler ghost + Confirmer primaire vert (.btn-p, owner 2026-09-10) */}
              <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={() => setShowAcompteModal(false)} className="btn-g px-4 py-2.5">Annuler</button>
                <button
                  type="submit"
                  disabled={applyingAcompte}
                  className="px-6 py-2.5 btn-p transition-colors"
                >
                  {applyingAcompte ? 'Enregistrement...' : "Confirmer l'acompte"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Supplier Modal */}
      {isEditModalOpen && (
        <div className="modal-backdrop z-50 flex items-center justify-center p-4" onClick={handleCloseEditModal}>
          <div className="relative w-full max-w-2xl mx-auto my-6" onClick={(e) => e.stopPropagation()}>
            <div className="relative flex flex-col w-full bg-white rounded-2xl shadow-lg max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="modal-head p-5">
                <h3 className="modal-title text-lg">Modifier — {supplier.name}</h3>
                <button
                  className="text-gray-400 hover:text-white text-xl"
                  onClick={handleCloseEditModal}
                  aria-label="Fermer"
                >
                  <IcClose />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 overflow-y-auto">
                {updateError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4" role="alert">
                    {updateError}
                  </div>
                )}

                <form onSubmit={handleUpdateSupplier} id="edit-supplier-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { name: 'name', label: 'Nom*', type: 'text', placeholder: 'Nom du fournisseur', required: true },
                      { name: 'email', label: 'Email', type: 'email', placeholder: 'Email du fournisseur' },
                      { name: 'phone', label: 'Téléphone', type: 'tel', placeholder: 'Numéro de téléphone' },
                      { name: 'address', label: 'Adresse', type: 'text', placeholder: 'Adresse du fournisseur' },
                      { name: 'registre_commercial', label: 'Registre Commercial', type: 'text', placeholder: 'Registre commercial' },
                      { name: 'matricule_fiscale', label: 'Matricule Fiscale', type: 'text', placeholder: 'Matricule fiscale' },
                    ].map((field) => (
                      <div key={field.name}>
                        <label className="block text-sm font-medium text-gray-900 mb-1.5" htmlFor={field.name}>
                          {field.label}
                        </label>
                        <input
                          type={field.type}
                          id={field.name}
                          name={field.name}
                          value={editFormData[field.name]}
                          onChange={handleEditFormChange}
                          className="inp w-full px-3 py-2.5 rounded-xl text-base text-gray-900"
                          placeholder={field.placeholder}
                          required={field.required}
                        />
                      </div>
                    ))}

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-600 mb-1.5" htmlFor="rib">RIB</label>
                      <input
                        type="text"
                        id="rib"
                        name="rib"
                        value={editFormData.rib}
                        onChange={handleEditFormChange}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-mono focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                        placeholder="RIB"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-600 mb-1.5" htmlFor="bank_name">Nom de la banque</label>
                      <input
                        type="text"
                        id="bank_name"
                        name="bank_name"
                        value={editFormData.bank_name}
                        onChange={handleEditFormChange}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                        placeholder="Nom de la banque"
                      />
                    </div>
                  </div>
                </form>
              </div>

              {/* Footer — delete on the left, save on the right */}
              <div className="flex items-center justify-between gap-2 p-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleDeleteSupplier}
                  disabled={deleting}
                  className="flex items-center py-2.5 px-4 btn-db transition-colors"
                >
                  <IcTrash className="mr-2" /> {deleting ? 'Suppression...' : 'Supprimer'}
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="px-4 py-2.5 btn-g transition-colors"
                    onClick={handleCloseEditModal}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    form="edit-supplier-form"
                    disabled={updating}
                    className="flex items-center py-2.5 px-5 btn-p transition-colors"
                  >
                    {updating ? (
                      <>
                        <BtnSk />
                        Mise à jour...
                      </>
                    ) : (
                      <>
                        <IcSave className="mr-2" /> Enregistrer
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Cases Coordonnées / Légales / Bancaires — page et panneau.
  const infoCases = (
    <>
        {/* Three info cases */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Coordonnées */}
          <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
            <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
              <span className="h-7 w-7 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center"><IcBuilding className="text-xs" /></span>
              Coordonnées
            </h4>
            <div className="space-y-3.5">
              <InfoRow Icon={IcMail} label="Email" value={supplier.email} />
              <InfoRow Icon={IcPhone} label="Téléphone" value={supplier.phone ? <PhoneLink phone={supplier.phone} /> : null} />
              <InfoRow Icon={IcMapPin} label="Adresse" value={supplier.address} />
            </div>
          </div>

          {/* Légales */}
          <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
            <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
              <span className="h-7 w-7 rounded-lg bg-gray-100 text-iface-600 flex items-center justify-center"><IcContact className="text-xs" /></span>
              Informations légales
            </h4>
            <div className="space-y-3.5">
              <InfoRow Icon={IcContact} label="Registre Commercial" value={supplier.registre_commercial} mono />
              <InfoRow Icon={IcContact} label="Matricule Fiscale" value={supplier.matricule_fiscale} mono />
            </div>
          </div>

          {/* Bancaires */}
          <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
            <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
              <span className="h-7 w-7 rounded-lg bg-green-100 text-green-600 flex items-center justify-center"><IcLandmark className="text-xs" /></span>
              Informations bancaires
            </h4>
            <div className="space-y-3.5">
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">RIB</p>
                <p className="text-sm text-gray-800 font-mono bg-white p-3 rounded-xl border border-gray-100 break-all">
                  {supplier.rib || 'Non renseigné'}
                </p>
              </div>
              <InfoRow Icon={IcLandmark} label="Nom de la banque" value={supplier.bank_name} />
            </div>
          </div>
        </div>

    </>
  );

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
  const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '');
  const PAYMENT_SRC = {
    // Sélection Studio owner 2026-09-10 : « Acompte » et « Achat et paiement »
    // au kit « Catégorie (texte seul) » (.bdg-cat) ; « Paiement facture » non relevé.
    acompte: { label: 'Acompte', badge: 'bdg-cat' },
    facture: { label: 'Paiement facture', badge: 'bdg-pos' },
  };

  // ======================= PANNEAU (depuis la liste) =======================
  // Même squelette que la fiche client (CustomerDetailModal) : entête encre
  // (nom + contact + ✕), cases de stats (gauche empilée / droite Reste à
  // payer + Acompte), UN cadre d'historique à deux onglets, pied Modifier.
  if (embedded) {
    return (
      <>
        {/* Entête */}
        <div className="modal-head p-6">
          <div className="flex-1 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="modal-title text-xl md:text-2xl break-words min-w-0">{supplier.name}</h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-gray-300">
                {supplier.phone && (
                  <span className="inline-flex items-center gap-1.5"><IcPhone className="text-blue-600" size={12} /><PhoneLink phone={supplier.phone} /></span>
                )}
                {supplier.email && (
                  <span className="inline-flex items-center gap-1.5 max-w-full"><IcMail className="text-blue-600 shrink-0" size={12} /><span className="break-all">{supplier.email}</span></span>
                )}
                {supplier.address && (
                  <span className="inline-flex items-center gap-1.5 max-w-full"><IcMapPin className="text-blue-600 shrink-0" size={12} /><span className="break-words min-w-0">{supplier.address}</span></span>
                )}
              </div>
              {/* « Afficher les détails » + le RIB au bout de la même ligne
                  (habillage du code produit : 12px gray-300 font-medium) — Ahmed 2026-09-10. */}
              <div className="mt-3 flex items-center gap-x-4">
                <button
                  type="button"
                  onClick={() => setShowDetails((v) => !v)}
                  aria-expanded={showDetails}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                >
                  {showDetails
                    ? <><IcChevronUp className="text-xs" /> Masquer les détails</>
                    : <><IcChevronDown className="text-xs" /> Afficher les détails</>}
                </button>
                {supplier.rib && (
                  <span className="ml-auto text-xs text-gray-300 font-medium whitespace-nowrap">{supplier.rib}</span>
                )}
              </div>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-white p-2 rounded-full shrink-0" aria-label="Fermer">
              <IcClose className="text-xl" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Coordonnées / Légales / Bancaires — dépliées depuis l'entête */}
          {showDetails && <div className="mb-4">{infoCases}</div>}

          {canManage && (
            <>
              {/* Cases de stats — même grille que la fiche client */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                <div className="flex flex-col gap-3 min-w-0">
                  <div className="bg-slate-50 rounded-xl shadow-sm border border-slate-200 px-3 py-2.5 flex items-center justify-between flex-1 overflow-hidden">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-500">Total facturé</p>
                      <p className="text-sm md:text-base font-bold text-gray-900 tabular-nums leading-tight">{formatDT(facturesStats.total)}</p>
                    </div>
                    <span className="h-7 w-7 rounded-lg hidden md:flex items-center justify-center bg-white text-slate-500 shrink-0"><IcReceiptText className="text-xs" /></span>
                  </div>
                  <div className="bg-slate-50 rounded-xl shadow-sm border border-slate-200 px-3 py-2.5 flex items-center justify-between flex-1 overflow-hidden">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-500">Montant payé</p>
                      <p className="text-sm md:text-base font-bold text-gray-900 tabular-nums leading-tight">{formatDT(facturesStats.paid)}</p>
                    </div>
                  </div>
                </div>

                <div className={`md:col-span-2 min-w-0 rounded-xl shadow-sm border px-3 md:px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-3 overflow-hidden ${amountDue > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`h-8 w-8 rounded-lg flex items-center justify-center bg-white shrink-0 ${amountDue > 0 ? 'text-red-600' : 'text-green-600'}`}><IcBanknote /></span>
                      <span className="text-sm font-semibold text-gray-600">Reste à payer</span>
                    </div>
                    <p className={`text-xl md:text-2xl font-bold mt-1 tabular-nums leading-tight ${amountDue > 0 ? 'text-red-700' : 'text-green-700'}`}>{formatDT(amountDue)}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{factureCount} facture(s) enregistrée(s)</p>
                  </div>
                  {amountDue > 0.0005 && (
                    <button
                      onClick={handleOpenAcompteModal}
                      className="btn-p shrink-0 px-4 py-2 text-sm transition-colors"
                    >
                      <IcBanknote /> Acompte
                    </button>
                  )}
                </div>
              </div>

              {/* Historique — un cadre, deux onglets */}
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-3 py-2 border-b border-gray-100 bg-slate-50 flex flex-wrap items-center gap-1.5">
                  {[
                    { key: 'paiements', label: 'Historique de paiement' },
                    { key: 'factures', label: 'Historique des factures' },
                  ].map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setHistoryTab(t.key)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        historyTab === t.key ? 'bg-gray-100 border border-gray-500 text-gray-700' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                  <span className="ml-auto text-xs text-gray-400 pr-1">
                    {historyTab === 'paiements'
                      ? `${payments.length} paiement${payments.length > 1 ? 's' : ''} · ${formatDT(paymentsTotal)}`
                      : `${factures.length} facture${factures.length > 1 ? 's' : ''} au total`}
                  </span>
                </div>

                {/* Filtres factures : recherche + statut + tri, sur une ligne */}
                {historyTab === 'factures' && factures.length > 0 && (
                  <div className="px-3 py-2 border-b border-gray-100 flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[180px]">
                      <IcSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                      <input
                        type="text"
                        value={historySearch}
                        onChange={(e) => setHistorySearch(e.target.value)}
                        placeholder="N° de facture..."
                        className="inp w-full pl-9 pr-8 py-1.5 text-sm"
                      />
                      {historySearch && (
                        <button type="button" onClick={() => setHistorySearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label="Effacer">
                          <IcClose size={14} />
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {STATUS_TABS.map((tab) => (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setHistoryStatus(tab.key)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors border ${
                            historyStatus === tab.key
                              ? 'bg-gray-900 border-gray-900 text-white'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {tab.label} <span className={historyStatus === tab.key ? 'text-gray-300' : 'text-gray-400'}>{tab.count}</span>
                        </button>
                      ))}
                    </div>
                    <select
                      value={historySort}
                      onChange={(e) => setHistorySort(e.target.value)}
                      className="inp px-3 py-1.5 text-xs text-gray-700"
                      aria-label="Trier"
                    >
                      <option value="recent">Plus récentes</option>
                      <option value="oldest">Plus anciennes</option>
                      <option value="amount">Montant (élevé)</option>
                    </select>
                  </div>
                )}

                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  {historyTab === 'paiements' ? (
                    paymentsLoading ? (
                      <div className="flex justify-center items-center py-14">
                        <CardSkeleton />
                      </div>
                    ) : payments.length === 0 ? (
                      <div className="empty-ki m-4">
                        <IcBanknote className="mx-auto text-3xl text-gray-200 mb-2" />
                        <p className="text-gray-500">Aucun paiement enregistré pour ce fournisseur.</p>
                      </div>
                    ) : (
                      <table className="min-w-full">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                            <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Note</th>
                            <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Montant</th>
                            <th className="px-5 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Reçu</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((pmt) => {
                            const src = PAYMENT_SRC[pmt.source] || { label: 'Achat et paiement', badge: 'bdg-cat' };
                            return (
                              <tr key={pmt.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                                <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {fmtDate(pmt.created_at)}
                                  {pmt.created_at && <span className="text-xs text-gray-400"> · {fmtTime(pmt.created_at)}</span>}
                                </td>
                                <td className="px-5 py-3 whitespace-nowrap"><span className={`bdg ${src.badge}`}>{src.label}</span></td>
                                <td className="px-5 py-3 text-sm text-gray-500 max-w-xs truncate">{pmt.note || <span className="text-gray-300">—</span>}</td>
                                <td className="px-5 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 tabular-nums">{formatDT(pmt.amount)}</td>
                                <td className="px-5 py-3 whitespace-nowrap text-right">
                                  <button
                                    type="button"
                                    onClick={() => openPhotoModal(pmt)}
                                    title={pmt.image_url ? 'Voir / changer le reçu de paiement' : 'Ajouter un reçu de paiement'}
                                    aria-label={pmt.image_url ? 'Voir ou changer le reçu de paiement' : 'Ajouter un reçu de paiement'}
                                    className={`inline-flex h-8 w-8 rounded-lg items-center justify-center overflow-hidden border transition-colors ${
                                      pmt.image_url
                                        ? 'bg-gray-700 hover:bg-gray-800 text-white border-gray-700'
                                        : 'border-dashed border-gray-300 text-gray-400 hover:text-emerald-600 hover:border-emerald-400 hover:bg-emerald-50'
                                    }`}
                                  >
                                    {pmt.image_url ? <img src={pmt.image_url} alt="" className="h-full w-full object-cover" /> : <IcCamera size={14} />}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )
                  ) : facturesLoading ? (
                    <div className="flex justify-center items-center py-14">
                      <CardSkeleton />
                    </div>
                  ) : displayedFactures.length === 0 ? (
                    <div className="empty-ki m-4">
                      <IcReceiptText className="mx-auto text-3xl text-gray-200 mb-2" />
                      <p className="text-gray-500">
                        {factures.length === 0 ? 'Aucune facture pour ce fournisseur.' : 'Aucune facture ne correspond à ce filtre.'}
                      </p>
                    </div>
                  ) : (
                    <table className="min-w-full">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Facture</th>
                          <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Montant</th>
                          <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut</th>
                          <th className="px-5 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedFactures.map((facture) => {
                          const meta = getStatusMeta(facture);
                          const total = Number(facture.total) || 0;
                          const due = Number(facture.amount_due) || 0;
                          const code = facture.number || facture.barrecode || 'N/A';
                          return (
                            <tr
                              key={facture.id || facture.barrecode}
                              onClick={() => openFacturePage(facture)}
                              className="border-t border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                            >
                              <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500">{fmtDate(facture.date_added)}</td>
                              <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-700">
                                Facture <span className="text-gray-400">{code}</span>
                                {facture.payment_method && <span className="text-xs text-gray-400"> · {facture.payment_method}</span>}
                              </td>
                              <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900 tabular-nums">{formatDT(total)}</td>
                              <td className="px-5 py-3 whitespace-nowrap">
                                {/* Sélection Studio owner 2026-09-10 : libellés sans symbole ;
                                    facture partielle = un seul badge « Reste X DT » au kit
                                    .bdg-alert (le badge « Partiel » est retiré ici). */}
                                {meta.key === 'partial' ? (
                                  <span className="bdg bdg-alert tabular-nums">Reste {formatDT(due)}</span>
                                ) : (
                                  <span className={`bdg ${meta.pill}`}>{meta.label}</span>
                                )}
                              </td>
                              <td className="px-5 py-3 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); openFacturePage(facture); }}
                                  className="font-semibold text-iface-600 hover:text-gray-900 inline-flex items-center justify-end gap-1"
                                >
                                  <IcReceipt className="text-xs" /> Détails
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Pied — « Fermer » à gauche (le RIB est monté dans l'entête), Modifier à droite */}
        <div className="flex justify-between items-center gap-3 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="btn-g py-2.5 px-5 transition-colors shrink-0">
            Fermer
          </button>
          {canManage && (
            <button
              onClick={handleOpenEditModal}
              className="btn-g-edit py-2.5 px-5 transition-colors shrink-0"
            >
              <IcPencil className="mr-2" /> Modifier
            </button>
          )}
        </div>

        {modals}
      </>
    );
  }

  return (
    <div className={embedded ? 'w-full' : 'w-full px-4 pt-2 pb-4'}>
      {/* Back link — en panneau, la croix de l'entête suffit */}
      {!embedded && (
        <button
          onClick={backToList}
          className="flex items-center px-5 py-2.5 text-sm mb-4 btn-s transition-colors"
        >
          <IcArrowLeft className="mr-2" /> Retour
        </button>
      )}

      {/* Combined identity + info card */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        {/* Identity row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            {/* Avatar fournisseur (sélection Ahmed 2026-08-16) : plus
                d'initiales — camion sur fond gris clair bordé. */}
            <div className="h-16 w-16 shrink-0 rounded-2xl bg-gray-100 border-[1.5px] border-gray-500 text-gray-500 flex items-center justify-center shadow-sm">
              <IcTruck className="text-2xl" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-800 truncate">{supplier.name}</h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
                {supplier.phone && <span className="flex items-center gap-1.5"><IcPhone className="text-xs text-gray-400" /><PhoneLink phone={supplier.phone} /></span>}
                {supplier.email && <span className="flex items-center gap-1.5"><IcMail className="text-xs text-gray-400" />{supplier.email}</span>}
              </div>
              <button
                type="button"
                onClick={() => setShowDetails((v) => !v)}
                aria-expanded={showDetails}
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                {showDetails
                  ? <><IcChevronUp className="text-xs" /> Masquer les détails</>
                  : <><IcChevronDown className="text-xs" /> Afficher les détails</>}
              </button>
            </div>
          </div>
        </div>

        {/* Divider + info cases — toggled by the show/hide button under the name */}
        {showDetails && (
        <>
        <div className="border-t border-gray-100 my-6" />

        {/* Mobile uniquement : cases « Factures » et « Total facturé » avec le
            nom du fournisseur dans « Afficher les détails » (demande Ahmed
            2026-08-20) — sur PC elles sont dans la rangée de stats plus bas.
            Données financières → manager/admin seulement, comme les stats. */}
        {canManage && (
          <div className="sm:hidden grid grid-cols-2 gap-3 mb-4">
            <div className="kpi rounded-2xl">
              <div className="flex items-center justify-between mb-2">
                <span className="kpi-label uppercase">Factures</span>
                <span className="h-8 w-8 rounded-xl flex items-center justify-center bg-white text-iface-600"><IcReceiptText /></span>
              </div>
              <p className="kpi-value text-xl text-gray-900">{factureCount}</p>
              <p className="kpi-sub mt-1">facture(s) enregistrée(s)</p>
            </div>
            <div className="kpi rounded-2xl">
              <div className="flex items-center justify-between mb-2">
                <span className="kpi-label uppercase">Total facturé</span>
                <span className="h-8 w-8 rounded-xl flex items-center justify-center bg-white text-iface-600"><IcReceiptText /></span>
              </div>
              <p className="kpi-value text-xl">{formatDT(facturesStats.total)}</p>
              <p className="kpi-sub mt-1">Payé {formatDT(facturesStats.paid)}</p>
            </div>
          </div>
        )}

        {infoCases}

        {/* Bouton Modifier — déplacé dans « Afficher les détails » (sélection
            Studio 2026-08-17, avant : en haut à droite de la carte identité). */}
        {canManage && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleOpenEditModal}
              className="flex items-center justify-center py-2.5 px-4 btn-g-edit transition-colors shrink-0"
            >
              <IcPencil className="mr-2" /> Modifier
            </button>
          </div>
        )}
        </>
        )}
      </div>

      {/* Financial sections (stats, paiements, factures) — manager/admin only.
          Sellers see only the supplier's information above. */}
      {canManage && (
      <>
      {/* Stat cards — cases « Factures » et « Total facturé » de retour avec
          « Reste à payer » (demande Ahmed 2026-08-20, annule la sélection
          Studio 2026-08-17). Sur mobile, ces deux cases vivent dans
          « Afficher les détails » de la carte identité (voir plus haut). */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {/* Cartes KPI (Composants-2 §5) : icône 16px en haut à droite. */}
        <div className="hidden sm:block kpi rounded-2xl">
          <IcReceiptText size={16} className="kpi-icon" />
          <p className="kpi-label uppercase pr-6 mb-2">Factures</p>
          <p className="kpi-value">{factureCount}</p>
          <p className="kpi-sub mt-1">facture(s) enregistrée(s)</p>
        </div>
        <div className="hidden sm:block kpi rounded-2xl">
          <IcReceiptText size={16} className="kpi-icon" />
          <p className="kpi-label uppercase pr-6 mb-2">Total facturé</p>
          <p className="kpi-value">{formatDT(facturesStats.total)}</p>
          <p className="kpi-sub mt-1">Payé {formatDT(facturesStats.paid)}</p>
        </div>
        <div className={`kpi rounded-2xl cursor-default ${amountDue > 0 ? 'kpi-coral' : 'kpi-green'}`}>
          <IcBanknote size={16} className="kpi-icon" />
          <p className="kpi-label uppercase pr-6 mb-2">Reste à payer</p>
          <p className="kpi-value">{formatDT(amountDue)}</p>
          {canManage && amountDue > 0 ? (
            <button
              onClick={handleOpenAcompteModal}
              className="mt-3 w-full flex items-center justify-center gap-2 text-sm py-2 btn-p transition-colors"
            >
              <IcBanknote /> Enregistrer un acompte
            </button>
          ) : (
            amountDue <= 0 && <p className="kpi-sub mt-1">Aucun impayé</p>
          )}
        </div>
      </div>

      {/* ===== Historique de paiement ===== */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {/* Section header */}
        <div className="flex items-center gap-3 p-5 border-b border-gray-100">
          <span className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center"><IcBanknote /></span>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-800">Historique de paiement</h2>
            <p className="text-xs text-gray-400">{payments.length} paiement(s) · total {formatDT(paymentsTotal)}</p>
          </div>
          <button
            type="button"
            onClick={() => setShowPayments((v) => !v)}
            aria-expanded={showPayments}
            className="ml-auto shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
          >
            {showPayments
              ? <><IcChevronUp className="text-xs" /> Masquer les détails</>
              : <><IcChevronDown className="text-xs" /> Afficher les détails</>}
          </button>
        </div>

        {/* List — hidden until « Afficher les détails » */}
        {!showPayments ? null : paymentsLoading ? (
          <div className="flex justify-center items-center py-14">
            <CardSkeleton />
          </div>
        ) : payments.length === 0 ? (
          <div className="py-12 text-center">
            <IcBanknote className="mx-auto text-4xl text-gray-200 mb-3" />
            <p className="text-gray-500">Aucun paiement enregistré pour ce fournisseur</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 max-h-[50vh] overflow-y-auto">
            {payments.map((p) => {
              const srcMeta = {
                acompte: { icon: IcReceipt, label: 'Acompte', badge: 'bdg-warn' },
                facture: { icon: IcReceiptText, label: 'Paiement facture', badge: 'bdg-pos' },
              }[p.source] || { icon: IcShoppingBag, label: 'Achat et paiement', badge: 'bdg-pos2' };
              const SrcIcon = srcMeta.icon;
              const srcLabel = srcMeta.label;
              const when = p.created_at ? new Date(p.created_at) : null;
              const dateStr = when ? when.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
              const timeStr = when ? when.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
              return (
                <div key={p.id} className="flex items-center gap-4 px-5 py-4">
                  <span className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center bg-emerald-50 text-emerald-600">
                    <SrcIcon />
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800 flex items-center gap-1.5">
                      <IcCalendar className="text-xs text-gray-400" /> {dateStr}
                      {timeStr && <span className="text-xs font-normal text-gray-400">à {timeStr}</span>}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      <span className={`bdg ${srcMeta.badge}`}>{srcLabel}</span>
                      {p.note ? ` · ${p.note}` : ''}
                    </p>
                  </div>
                  <div className="ml-auto text-right shrink-0">
                    <p className="text-lg font-bold text-emerald-700">{formatDT(p.amount)}</p>
                  </div>

                  {/* Reçu de paiement — thumbnail if attached, camera button otherwise */}
                  <button
                    type="button"
                    onClick={() => openPhotoModal(p)}
                    title={p.image_url ? 'Voir / changer le reçu de paiement' : 'Ajouter un reçu de paiement'}
                    aria-label={p.image_url ? 'Voir ou changer le reçu de paiement' : 'Ajouter un reçu de paiement'}
                    className={`h-10 w-10 shrink-0 rounded-xl flex items-center justify-center overflow-hidden border transition-colors ${
                      p.image_url
                        ? 'bg-gray-700 hover:bg-gray-800 text-white border-gray-700'
                        : 'border-dashed border-gray-300 text-gray-400 hover:text-emerald-600 hover:border-emerald-400 hover:bg-emerald-50'
                    }`}
                  >
                    {p.image_url ? (
                      <img src={p.image_url} alt="Reçu de paiement" className="h-full w-full object-cover" />
                    ) : (
                      <IcCamera />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ===== Historique des factures ===== */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mt-6">
        {/* Section header */}
        <div className="flex items-center gap-3 p-5 border-b border-gray-100">
          <span className="h-10 w-10 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center"><IcReceiptText /></span>
          <div>
            <h2 className="text-lg font-bold text-gray-800">Historique des factures</h2>
            <p className="text-xs text-gray-400">{factures.length} facture(s) · sélectionnez-en une pour voir le détail</p>
          </div>
          <button
            type="button"
            onClick={() => setShowFactures((v) => !v)}
            aria-expanded={showFactures}
            className="ml-auto shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
          >
            {showFactures
              ? <><IcChevronUp className="text-xs" /> Masquer les détails</>
              : <><IcChevronDown className="text-xs" /> Afficher les détails</>}
          </button>
        </div>

        {showFactures && (
        <>
        {/* Toolbar: search + sort + status tabs */}
        <div className="p-5 pb-3 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                placeholder="Rechercher par n° de facture..."
                className="w-full pl-11 pr-10 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 bg-white"
              />
              {historySearch && (
                <button
                  type="button"
                  onClick={() => setHistorySearch('')}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <IcClose />
                </button>
              )}
            </div>
            <select
              value={historySort}
              onChange={(e) => setHistorySort(e.target.value)}
              className="inp px-4 py-2.5 text-sm text-gray-700"
            >
              <option value="recent">Plus récentes</option>
              <option value="oldest">Plus anciennes</option>
              <option value="amount">Montant (élevé)</option>
            </select>
          </div>

          {/* Status filter tabs */}
          <div className="flex flex-wrap gap-2">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setHistoryStatus(tab.key)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                  historyStatus === tab.key
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {tab.label}
                <span className={`ml-1.5 ${historyStatus === tab.key ? 'text-blue-100' : 'text-gray-400'}`}>{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {facturesLoading ? (
          <div className="flex justify-center items-center py-16">
            <CardSkeleton />
          </div>
        ) : displayedFactures.length === 0 ? (
          <div className="py-14 text-center">
            <IcReceiptText className="mx-auto text-4xl text-gray-200 mb-3" />
            <p className="text-gray-500">
              {factures.length === 0 ? 'Aucune facture pour ce fournisseur' : 'Aucune facture ne correspond à ce filtre'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
            {displayedFactures.map((facture) => {
              const meta = getStatusMeta(facture);
              const total = Number(facture.total) || 0;
              const due = Number(facture.amount_due) || 0;
              const paid = total - due;
              const pct = total > 0 ? Math.min(100, Math.max(0, (paid / total) * 100)) : (due <= 0 ? 100 : 0);
              const code = facture.number || facture.barrecode || 'N/A';
              const dateStr = facture.date_added ? new Date(facture.date_added).toLocaleDateString('fr-FR') : '';

              return (
                <button
                  key={facture.id || facture.barrecode}
                  type="button"
                  onClick={() => openFacturePage(facture)}
                  className="w-full text-left flex items-center gap-4 px-5 py-4 tr-ki transition-colors group"
                >
                    {/* Status icon */}
                    <span className={`h-10 w-10 shrink-0 rounded-xl flex items-center justify-center ${meta.pill}`}>
                      <meta.Icon />
                    </span>

                    {/* Code + date */}
                    <div className="min-w-0 w-32 sm:w-40">
                      <p className="font-semibold text-gray-800 truncate">Facture {code}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{dateStr}{facture.payment_method ? ` · ${facture.payment_method}` : ''}</p>
                    </div>

                    {/* Progress (hidden on small screens) */}
                    <div className="hidden md:block flex-1 min-w-0">
                      <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${meta.bar}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="flex justify-between text-xs mt-1.5">
                        <span className="text-gray-400">Payé {formatDT(paid)}</span>
                        {due > 0
                          ? <span className="text-red-600 font-medium">Reste {formatDT(due)}</span>
                          : <span className="text-green-600 font-medium">Soldée</span>}
                      </div>
                    </div>

                    {/* Total + status pill */}
                    <div className="ml-auto text-right shrink-0">
                      <p className="text-lg font-bold text-gray-900">{formatDT(total)}</p>
                      <span className={`bdg mt-0.5 ${meta.pill}`}>{meta.label}</span>
                    </div>

                  <IcChevronRight className="text-gray-300 group-hover:text-gray-400 shrink-0" />
                </button>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>
      </>
      )}

      {modals}
    </div>
  );
};

export default SupplierDetail;
